import { useState, useCallback, useRef } from "react";
import { useNavigate } from "react-router-dom";

import { logoutProcessor } from "./helpers/logoutProcessor";
import { playSuccess, playError } from "./helpers/audioPlayer";
import { ArrowLeft, CheckCircle } from "lucide-react";
import { toast } from "react-toastify";
import { useTranslation } from "../i18n";
import HiddenScanInput from "./HiddenScanInput";
import FlowLoader from "./FlowLoader";
import ScanStatusIcons from "./ScanStatusIcons";
import ScanFlashOverlay from "./ScanFlashOverlay";
import FitWord from "./FitWord";
import { apiFetch } from "../api/apiFetch";

type ConveyorSearchProps = {
    goBack: () => void;
};

const ConveyorSearch = ({ goBack }: ConveyorSearchProps) => {
    const navigator = useNavigate();
    const { t } = useTranslation();

    const [scan, setScan] = useState<string>("");
    const [scanStatus, set_scanStatus] = useState<"idle" | "success" | "error">("idle");
    const statusTimerRef = useRef<NodeJS.Timeout | null>(null);
    const [loading, setLoading] = useState<boolean>(false);

    const [chute, set_chute] = useState<number | null>(null);
    const [zone, set_zone] = useState<number | null>(null);
    const [hasResult, set_hasResult] = useState<boolean>(false);
    const [itemDetail, set_itemDetail] = useState<any>(null);

    const userData = window.localStorage.getItem("user");
    const userObject = userData !== null ? JSON.parse(userData) : { warehouse: "Newport" };

    const flashScanStatus = (status: "success" | "error") => {
        if (statusTimerRef.current) clearTimeout(statusTimerRef.current);
        set_scanStatus(status);
        statusTimerRef.current = setTimeout(() => set_scanStatus("idle"), 500);
    };

    const fetchItemDetail = async (itemSku: string) => {
        try {
            const res = await apiFetch(`/zonestorage/settings/item-search`, {
                method: "POST",
                headers: { "Content-Type": "application/json" },
                body: JSON.stringify({ warehouse: userObject.warehouse || "Newport", itemSku })
            });

            if (res.ok) {
                const data = await res.json();
                if (data.item && data.item.length > 0) {
                    set_itemDetail(data.item[0]);
                    set_hasResult(true);
                    flashScanStatus("success");
                    playSuccess();
                } else {
                    set_hasResult(false);
                    set_itemDetail(null);
                    flashScanStatus("error");
                    playError();
                    toast.dismiss(); toast.error(t("product_not_found"));
                }
            } else {
                set_hasResult(false);
                set_itemDetail(null);
                flashScanStatus("error");
                playError();
                toast.dismiss(); toast.error(t("product_not_found"));
            }
        } catch {
            set_hasResult(false);
            set_itemDetail(null);
            flashScanStatus("error");
            playError();
            toast.dismiss(); toast.error(t("scan_failed"));
        }
    };

    const doSubmit = useCallback(async (scannedValue?: string) => {
        const val = scannedValue ?? scan;
        if (val.trim() === "") return;

        const scannedSku = val.trim();
        setScan("");
        setLoading(true);

        try {
            const res = await apiFetch(`/zonestorage/iteminfo/search-at-conveyor`, {
                method: "POST",
                headers: { "Content-Type": "application/json" },
                body: JSON.stringify({ itemSku: scannedSku })
            });

            if (res.ok) {
                const data = await res.json();
                if (data.chute) {
                    set_chute(data.chute);
                    set_zone(data.zone);
                    set_itemDetail(null);
                    set_hasResult(true);
                    flashScanStatus("success");
                    playSuccess();
                } else {
                    // No chute — fallback to item search
                    set_chute(null);
                    set_zone(null);
                    await fetchItemDetail(scannedSku);
                }
            } else if (res.status === 401) {
                logoutProcessor();
                navigator("/login");
            } else {
                // First endpoint failed — try fallback item search
                set_chute(null);
                set_zone(null);
                await fetchItemDetail(scannedSku);
            }
        } catch {
            set_hasResult(false);
            set_itemDetail(null);
            flashScanStatus("error");
            playError();
            toast.dismiss(); toast.error(t("scan_failed"));
        }

        setLoading(false);
    }, [scan, navigator, t]);

    return (
        <div className="scan-panel" style={{ backgroundColor: scanStatus === "success" ? "rgba(34, 197, 94, 0.18)" : undefined }}>
            <ScanFlashOverlay active={scanStatus === "error"} />
            <div className="page-title-bar">
                <h1 className="page-title">{t("search")}</h1>
                <button className="page-back-btn" onClick={goBack}><ArrowLeft size={20} /></button>
            </div>

            <div className={`scan-centre ${hasResult ? "conveyor-search-centre" : ""}`}>
                {!hasResult && <ScanStatusIcons status={scanStatus === "error" ? "idle" : scanStatus} />}

                <div className="conveyor-scan-group" style={{ overflow: "hidden" }}>
                    {!hasResult && (
                        <div className={`scan-command ${scanStatus !== "idle" ? "scan-command-hidden" : ""}`}>
                            {t("scan_item").toUpperCase().split(" ").map((word, i) => (
                                <FitWord key={i}>{word}</FitWord>
                            ))}
                        </div>
                    )}

                    {loading && <FlowLoader />}

                    {hasResult && !loading && chute !== null && (
                        <div className={`scan-feedback-area conveyor-search-results ${scanStatus !== "idle" ? "scan-command-hidden" : ""}`}>
                            <span className="conveyor-search-label">{t("chute_info", { chute: "" }).toUpperCase()}</span>
                            <h1 className="conveyor-search-result">{chute}</h1>
                            {zone !== null && (
                                <>
                                    <span className="conveyor-search-label conveyor-search-label-sub">{t("zone_info", { zone: "" }).toUpperCase()}</span>
                                    <h2 className="conveyor-search-result-sub">{zone}</h2>
                                </>
                            )}
                        </div>
                    )}

                    {hasResult && !loading && chute === null && itemDetail && (
                        <div className={`conveyor-search-detail-card ${scanStatus !== "idle" ? "scan-command-hidden" : ""}`}>
                            <h2 className="conveyor-search-detail-title">{t("item_details")}</h2>
                            <div className="conveyor-search-detail-row">
                                <span className="conveyor-search-detail-label">{t("day")}</span>
                                <span className="conveyor-search-detail-value">{new Date(itemDetail.Day).toLocaleDateString()}</span>
                            </div>
                            <div className="conveyor-search-detail-row">
                                <span className="conveyor-search-detail-label">{t("order_number")}</span>
                                <span className="conveyor-search-detail-value">{itemDetail.OrderNumber}</span>
                            </div>
                            <div className="conveyor-search-detail-row">
                                <span className="conveyor-search-detail-label">{t("batch_label", { batch: "" })}</span>
                                <span className="conveyor-search-detail-value">{itemDetail.Batch}</span>
                            </div>
                            <div className="conveyor-search-detail-row">
                                <span className="conveyor-search-detail-label">{t("zone_label", { zone: "" })}</span>
                                <span className="conveyor-search-detail-value">{itemDetail.Sorter1Location}</span>
                            </div>
                            <div className="conveyor-search-detail-row">
                                <span className="conveyor-search-detail-label">{t("sorter_1")}</span>
                                <span className="conveyor-search-detail-value">{itemDetail.Sorter1Scanned ? "✓" : "-"}</span>
                            </div>
                            <div className="conveyor-search-detail-row">
                                <span className="conveyor-search-detail-label">{t("sorter_2")}</span>
                                <span className="conveyor-search-detail-value">{itemDetail.Sorter2Scanned ? "✓" : "-"}</span>
                            </div>
                            <div className="conveyor-search-detail-row">
                                <span className="conveyor-search-detail-label">{t("status")}</span>
                                <span className="conveyor-search-detail-value">{itemDetail.Status}</span>
                            </div>
                            {itemDetail.Priority && (
                                <div className="conveyor-search-detail-row">
                                    <span className="conveyor-search-detail-label">{t("priority")}</span>
                                    <span className="conveyor-search-detail-value">Yes</span>
                                </div>
                            )}
                            {itemDetail.OrderDetails && (
                                <>
                                    <div className="conveyor-search-detail-row">
                                        <span className="conveyor-search-detail-label">{t("sent_to_ppd_label")}</span>
                                        <span className="conveyor-search-detail-value">{itemDetail.OrderDetails.SentToPPd ? "✓" : "-"}</span>
                                    </div>
                                </>
                            )}
                        </div>
                    )}
                </div>
            </div>
            {hasResult && scanStatus === "success" && (
                <div className="s2-inline-status s2-inline-success"><CheckCircle size={140} strokeWidth={3} /></div>
            )}

            <HiddenScanInput
                value={scan}
                onChange={setScan}
                onSubmit={doSubmit}
                disabled={loading}
            />
        </div>
    );
};

export default ConveyorSearch;
